// Public invite-acceptance page. Users land here from an emailed invite link.
// Reads the invite token from the URL, lets the invitee set firstName/lastName + password,
// then creates the account (ORG_ADMIN or ORG_USER) and redirects to login.
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { PanorixLogoCompact } from '@/components/PanorixLogo';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, invite: null, error: '' });
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/invitations/by-token/${encodeURIComponent(token)}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setState({ loading: false, invite: null, error: data.detail || 'Invitation invalid.' });
          return;
        }
        setFirstName(data.firstName || '');
        setLastName(data.lastName || '');
        setState({ loading: false, invite: data, error: '' });
      } catch (e) {
        if (!cancelled) setState({ loading: false, invite: null, error: 'Could not reach the server.' });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { toast.error('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/invitations/by-token/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, firstName, lastName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not accept invitation.');
      toast.success('Account activated. You can now sign in.');
      navigate('/?activated=1', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Failed.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#070B14] text-slate-100 flex flex-col" style={{ fontFamily: '"DM Sans", "Inter", sans-serif' }}>
      <header className="border-b border-slate-800/60 backdrop-blur-md bg-[#070B14]/80">
        <div className="max-w-[1200px] mx-auto px-8 h-16 flex items-center">
          <Link to="/"><PanorixLogoCompact size={140}/></Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md rounded-xl p-8" style={{ background: 'rgba(11,18,32,0.7)', border: '1px solid rgba(30,78,140,0.4)' }}>
          {state.loading ? (
            <div className="text-center py-10" data-testid="invite-loading">
              <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-70"/>
              <p className="text-xs tracking-[0.2em] uppercase text-slate-500 mt-3">Validating invitation…</p>
            </div>
          ) : state.error ? (
            <div className="text-center py-6" data-testid="invite-error">
              <AlertCircle className="w-10 h-10 mx-auto text-amber-400 mb-3"/>
              <h1 className="text-lg font-medium text-slate-100 mb-1">Invitation unavailable</h1>
              <p className="text-sm text-slate-400 mb-6">{state.error}</p>
              <Link to="/" className="text-xs tracking-[0.2em] uppercase text-slate-300 hover:text-white">← Back to home</Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-5 h-5 text-[#5790C9]"/>
                <h1 className="text-xl font-medium tracking-wide">Accept your invitation</h1>
              </div>
              <p className="text-sm text-slate-400 mb-5">
                You've been invited to <span className="text-slate-200">{state.invite?.organization?.name || 'Capexara'}</span> as a{' '}
                <span className="text-[#F26B1F] uppercase tracking-wider text-xs">{state.invite?.role === 'ORG_ADMIN' ? 'Organization Admin' : 'Team Member'}</span>.
              </p>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label className="text-[11px] tracking-[0.15em] text-slate-400 uppercase">Email</Label>
                  <Input value={state.invite?.email || ''} disabled data-testid="invite-email" className="bg-slate-900 border-slate-800 text-slate-300"/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] tracking-[0.15em] text-slate-400 uppercase">First name</Label>
                    <Input value={firstName} onChange={e => setFirstName(e.target.value)} required data-testid="invite-first-name" className="bg-slate-900 border-slate-800 text-slate-100"/>
                  </div>
                  <div>
                    <Label className="text-[11px] tracking-[0.15em] text-slate-400 uppercase">Last name</Label>
                    <Input value={lastName} onChange={e => setLastName(e.target.value)} data-testid="invite-last-name" className="bg-slate-900 border-slate-800 text-slate-100"/>
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] tracking-[0.15em] text-slate-400 uppercase">Password</Label>
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} data-testid="invite-password" className="bg-slate-900 border-slate-800 text-slate-100"/>
                  <p className="text-[10px] text-slate-500 mt-1">At least 8 characters.</p>
                </div>
                <div>
                  <Label className="text-[11px] tracking-[0.15em] text-slate-400 uppercase">Confirm password</Label>
                  <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required data-testid="invite-confirm" className="bg-slate-900 border-slate-800 text-slate-100"/>
                </div>
                <Button type="submit" disabled={submitting} data-testid="invite-submit" className="w-full bg-[#1E4E8C] hover:bg-[#143A6B] text-white tracking-[0.15em] uppercase text-[12px] mt-2">
                  {submitting ? 'Activating…' : 'Activate account'}
                </Button>
              </form>
              <p className="text-[10px] text-slate-600 mt-4 text-center">Invitation expires {state.invite?.expiresAt?.slice(0,10)}.</p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
